// src/features/hmis/types/index.ts
import { RaRecord } from 'react-admin';

// Exportar componentes principales
export { default as HMIList } from './components/HMIList';
export { default as HMICreate } from './components/HMICreate';
export { default as HMIEdit } from './components/HMIEdit';

// Enums matching backend
export enum HmiType {
  Tablet = 1,
  Kiosk = 2
}

// Base interfaces
export interface HMIBase {
  id: string | number; // Match RaRecord<Identifier> type
  name: string;
  deviceId: string;
  type: HmiType;
  isActive: boolean;
  hotelId: string;
  zoneId: string;
  createdAt: string;
  updatedAt: string;
}

// Zone summary for HMI
export interface ZoneSummary {
  id: string;
  name: string;
  description?: string;
}

// List item (used in DataGrid)
export interface HMIListItem extends HMIBase, RaRecord {
  zone: ZoneSummary;
  lastSeenAt?: string;
  isOnline: boolean;
  hasProvisioningCode: boolean;
  provisioningCodeExpiresAt?: string;
}

// Detail response (used in forms and show)
export interface HMIDetailResponse extends HMIBase, RaRecord {
  zone: ZoneSummary;
  lastSeenAt?: string;
  isOnline: boolean;
  hasProvisioningCode: boolean;
  provisioningCodeExpiresAt?: string;
  controllerIpAddress: string;
  controllerTcpPort: number;
  initialApiKey?: string; // Only included on creation
}

// Form data for create/edit
export interface HMIFormData {
  name: string;
  type: HmiType;
  hotelId: string;
  zoneId: string;
}

// Request DTOs
export interface CreateHMIRequest {
  name: string;
  type: HmiType;
  hotelId: string;
  zoneId: string;
}

export interface UpdateHMIRequest {
  name?: string;
  type?: HmiType;
  zoneId?: string;
}

export interface ProvisioningCodeResponse {
  provisioningCode: string;
  expiresAt: string;
}

export interface ApiKeyResetResponse {
  newApiKey: string;
  deviceId: string;
  message: string;
}

export interface ResetApiKeyRequest {
  reason: string;
}

// Controller configuration
export interface HMIControllerConfig {
  hmiControllerIp: string;
  tcpPort: number;
  sharedSecret: string;
  connectionTimeout: number;
  messageTimeout: number;
  retryAttempts: number;
}

// Helper functions
export const getHmiTypeLabel = (type: HmiType): string => {
  switch (type) {
    case HmiType.Tablet:
      return 'Tablet';
    case HmiType.Kiosk:
      return 'Kiosk';
    default:
      return 'Desconocido';
  }
};

export const getHmiTypeColor = (type: HmiType): 'primary' | 'secondary' => {
  switch (type) {
    case HmiType.Tablet:
      return 'primary';
    case HmiType.Kiosk:
      return 'secondary';
    default:
      return 'primary';
  }
};

export const getHmiTypeIcon = (type: HmiType): string => {
  switch (type) {
    case HmiType.Tablet:
      return '📱';
    case HmiType.Kiosk:
      return '🖥️';
    default:
      return '📱';
  }
};

export const getOnlineStatusLabel = (isOnline: boolean): string => {
  return isOnline ? 'En línea' : 'Desconectado';
};

export const getOnlineStatusColor = (isOnline: boolean): 'success' | 'error' => {
  return isOnline ? 'success' : 'error';
};

export const formatLastSeen = (lastSeenAt?: string): string => {
  if (!lastSeenAt) return 'Nunca';
  
  const date = new Date(lastSeenAt);
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
  
  if (diffInMinutes < 1) return 'Hace un momento';
  if (diffInMinutes < 60) return `Hace ${diffInMinutes} min`;
  if (diffInMinutes < 1440) return `Hace ${Math.floor(diffInMinutes / 60)} h`;
  
  return date.toLocaleDateString('es-ES');
};

export const isProvisioningCodeExpired = (expiresAt?: string): boolean => {
  if (!expiresAt) return true;
  return new Date(expiresAt) < new Date();
};

export const getProvisioningCodeStatus = (hasCode: boolean, expiresAt?: string): 'active' | 'expired' | 'none' => {
  if (!hasCode) return 'none';
  if (isProvisioningCodeExpired(expiresAt)) return 'expired';
  return 'active';
};

// Transform functions for API compatibility
export const prepareHMIForAPI = (data: HMIFormData): CreateHMIRequest => {
  return {
    name: data.name,
    type: data.type,
    hotelId: data.hotelId,
    zoneId: data.zoneId,
  };
};

// Validation helpers
export const isValidDeviceId = (deviceId: string): boolean => {
  return /^[A-Za-z0-9-_]+$/.test(deviceId) && deviceId.length >= 3 && deviceId.length <= 50;
};

export const isValidHMIName = (name: string): boolean => {
  return name.length >= 2 && name.length <= 100;
};

// Type choices for SelectInput
export const HMI_TYPE_CHOICES = [
  { id: HmiType.Tablet, name: getHmiTypeLabel(HmiType.Tablet) },
  { id: HmiType.Kiosk, name: getHmiTypeLabel(HmiType.Kiosk) },
];
